// Sessão 4: mock removido - kanban conectado ao backend real.
// Redesign (22/06/2026): header neutro, toolbar com busca por campo, freeze de colunas.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import KanbanColumn from '../components/KanbanColumn';
import NovaOperacaoModal from '../components/NovaOperacaoModal';
import ConfirmModal from '../components/ConfirmModal';
import OperacaoDetalheSheet from '../components/OperacaoDetalheSheet';
import type { Operacao, ColunasKanban, Esteira } from '../types';
import { operacoesApi, isolveApi, type ErroISolve } from '../services/api';
import { useSessaoISolve } from '../hooks/useSessaoISolve';
import { toastErroISolve } from '../utils/toastErroISolve';

// SPEC_LAYOUT_UX_V1 (E1) - 3 esteiras visuais: Crédito/Jurídico/Registro
const COLUNAS_VAZIAS: ColunasKanban = { 0: [], 1: [], 2: [], 3: [] };

// Campos disponíveis para busca client-side
const CAMPOS_BUSCA = [
  { value: 'codigo', label: 'Código OTM', placeholder: 'Ex: OTM-2026-0884 ou só 0884' },
  { value: 'nome',   label: 'Nome',    placeholder: 'Digite o nome...' },
  { value: 'cpf',    label: 'CPF',     placeholder: 'Digite o CPF...' },
  { value: 'offer',  label: 'OfferID', placeholder: 'Digite o Offer ID...' },
  { value: 'origem', label: 'Origem',  placeholder: '5A, S4 ou VAR' },
  { value: 'cidade', label: 'Cidade',  placeholder: 'Digite a cidade...' },
] as const;

type CampoBusca = typeof CAMPOS_BUSCA[number]['value'];

function filtrarOp(op: Operacao, campo: CampoBusca, termo: string): boolean {
  const t = termo.toLowerCase().trim();
  if (!t) return true;
  switch (campo) {
    // Achado do walkthrough Emerson (05/07/2026): "codigo" ausente - buscar
    // "0884" ou "OTM-2026-0884" tinha que cair em algum campo, e nenhum dos
    // existentes olhava numeroProcesso. Case-insensitive + substring parcial,
    // mesmo padrão dos demais campos desta função.
    case 'codigo':
      return op.numeroProcesso.toLowerCase().includes(t);
    case 'nome':
      return [...op.compradores, ...op.vendedores].some(p => p.nome.toLowerCase().includes(t));
    case 'cpf': {
      const tNum = t.replace(/\D/g, '');
      return [...op.compradores, ...op.vendedores].some(p => (p.cpf ?? '').replace(/\D/g, '').includes(tNum));
    }
    case 'origem':
      return op.origem.toLowerCase().includes(t);
    case 'cidade':
      return (op.imovelDetalhe?.cidade ?? '').toLowerCase().includes(t);
    case 'offer':
      return (op.offerId ?? '').toLowerCase().includes(t);
  }
}

export default function KanbanBoard() {
  const navigate = useNavigate();
  const [searchParams]                          = useSearchParams();
  const [colunas, setColunas]                   = useState<ColunasKanban>(COLUNAS_VAZIAS);
  const [carregando, setCarregando]             = useState(true);
  const [erro, setErro]                         = useState<string | null>(null);
  const [modalNova, setModalNova]               = useState(false);
  const [campoBusca, setCampoBusca]             = useState<CampoBusca>('nome');
  const [termoBusca, setTermoBusca]             = useState('');
  const [sincronizando, setSincronizando]       = useState(false);
  const [rodandoMassa, setRodandoMassa]         = useState(false);
  const [confirmCargaAberta, setConfirmCargaAberta] = useState(false);
  const [opDetalheId, setOpDetalheId]           = useState<string | null>(null);
  const { refetch: refetchSessao } = useSessaoISolve();

  const carregarKanban = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const dados = await operacoesApi.getKanban();
      setColunas(dados);
    } catch (e: any) {
      const msg = e?.response?.data?.erro ?? e?.message ?? 'Erro desconhecido';
      setErro(`Erro ao carregar operações: ${msg}`);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregarKanban(); }, [carregarKanban]);

  // Chega via link externo (?op=<id>) - ex.: Fila de Divergências. Redireciona
  // para a página dedicada da operação (TO-BE, 07/09/2026).
  useEffect(() => {
    const opId = searchParams.get('op');
    if (!opId) return;
    navigate(`/operacoes/${opId}`, { replace: true });
  }, [searchParams, navigate]);

  const sincronizar = useCallback(async () => {
    setSincronizando(true);
    try {
      const r = await isolveApi.sincronizar();
      const semPropostaTxt = r.ccvSemProposta.esqueletosCriados > 0 ? `, ${r.ccvSemProposta.esqueletosCriados} CCV sem proposta` : '';
      // MET-017/MET-018 (09/07/2026) - antes não existia nenhum contador de
      // "execuções do Sincronizar" em lugar nenhum da UI; agora aparece sempre,
      // mesmo em ciclos que não criaram/atualizaram nada.
      const resumo = `Sincronização: ${r.novasImportadas} novas, ${r.atualizadas} atualizadas, ${r.divergenciasEnfileiradas} divergências${semPropostaTxt} (${r.execucoesHoje}ª execução hoje)`;
      if (r.ccvSemProposta.recusadasP5a.length > 0) {
        const nomes = r.ccvSemProposta.recusadasP5a.map(x => x.clienteMascarado).join(', ');
        toast.warning(`${resumo}. ${r.ccvSemProposta.recusadasP5a.length} operação(ões) com CCV assinado e cadastro incompleto no iSolve (sem CPF): ${nomes} - preencher no iSolve.`, { duration: 10000 });
      }
      if (r.ccvSemProposta.tetoAtingido) {
        toast.warning(`Sincronização: teto de páginas do scan "Novos compradores" atingido (${r.ccvSemProposta.paginasLidas} páginas, ${r.ccvSemProposta.itensVistos} itens) - pode haver mais itens não escaneados. Avisar o time técnico.`, { duration: 10000 });
      }
      if (r.status === 'parcial') {
        toast.warning(`${resumo}. ${r.falhas.length} falha(s) - revisar.`, { duration: 8000 });
      } else if (r.divergenciasEnfileiradas > 0) {
        toast.success(resumo, {
          action: { label: 'Ver divergências', onClick: () => navigate('/divergencias') },
          duration: 8000,
        });
      } else {
        toast.success(resumo);
      }
      await carregarKanban();
    } catch (e: unknown) {
      const erroDiscriminador = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro as ErroISolve | undefined;
      if (erroDiscriminador) {
        toastErroISolve(erroDiscriminador, navigate);
      } else {
        toast.error('Erro ao sincronizar - tente novamente.');
      }
    } finally {
      setSincronizando(false);
      refetchSessao(); // atualiza semáforo após ação (self-heal ou flip)
    }
  }, [carregarKanban, navigate, refetchSessao]);

  // Filtro client-side sobre os dados já carregados
  const colunasFiltradas = useMemo<ColunasKanban>(() => {
    if (!termoBusca.trim()) return colunas;
    const filtrar = (ops: Operacao[]) => ops.filter(op => filtrarOp(op, campoBusca, termoBusca));
    return { 0: filtrar(colunas[0]), 1: filtrar(colunas[1]), 2: filtrar(colunas[2]), 3: filtrar(colunas[3]) };
  }, [colunas, campoBusca, termoBusca]);

  const totalOps = Object.values(colunas).flat().length;
  const totalFiltrado = Object.values(colunasFiltradas).flat().length;

  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const srcEsteira = Number(source.droppableId) as Esteira;
    const dstEsteira = Number(destination.droppableId) as Esteira;

    setColunas((prev) => {
      const next = { ...prev };
      const srcList = [...prev[srcEsteira]];
      const [moved] = srcList.splice(source.index, 1);
      const dstList = srcEsteira === dstEsteira ? srcList : [...prev[dstEsteira]];
      dstList.splice(destination.index, 0, { ...moved, esteira: dstEsteira });
      return { ...next, [srcEsteira]: srcList, [dstEsteira]: dstList };
    });

    if (srcEsteira !== dstEsteira) {
      try {
        await operacoesApi.moverEsteira(draggableId, dstEsteira);
      } catch (err: any) {
        const motivo: string = err?.response?.data?.erro ?? err?.message ?? 'Erro ao mover operação.';
        toast.error('Movimento bloqueado: ' + motivo);
        carregarKanban();
      }
    }
  };

  // Clicar no card abre o detalhe num MODAL LATERAL (~56% da tela), sem sair do
  // board. A rota /operacoes/:id continua existindo (deep-links).
  const abrirModal = (op: Operacao) => {
    setOpDetalheId(op.id);
  };

  // Carga inicial - só a aba Novas Operações (não cria card no Kanban).
  // A confirmação agora é um modal central (ConfirmModal), não window.confirm.
  const executarCargaMassa = useCallback(async () => {
    setRodandoMassa(true);
    try {
      const resultado = await isolveApi.importarMassa() as { enfileiradas?: number };
      const n = resultado.enfileiradas ?? 0;
      toast.success(`Carga inicial: ${n} candidata(s) em Entrada iSolve. Nenhuma operação criada no Kanban.`);
      await carregarKanban();
    } catch (e: any) {
      toast.error(e?.response?.data?.erro ?? e?.message ?? 'Erro na carga inicial.');
    } finally {
      setRodandoMassa(false);
      setConfirmCargaAberta(false);
    }
  }, [carregarKanban]);

  const campoDef = CAMPOS_BUSCA.find(c => c.value === campoBusca)!;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#F0F2F5' }}>

      {/* Toolbar de busca - sticky no topo da área de conteúdo */}
      <div className="sticky top-0 z-20 bg-white border-b border-[#E2E6ED] px-4 py-2.5 flex items-center gap-3">
          {/* Combo campo + input de busca */}
          <div className="flex items-center gap-0 border border-[#E2E6ED] rounded-[6px] overflow-hidden bg-white flex-1 max-w-md">
            <select
              value={campoBusca}
              onChange={e => { setCampoBusca(e.target.value as CampoBusca); setTermoBusca(''); }}
              className="text-[12px] text-[#5F6470] bg-[#F7F9FB] border-r border-[#E2E6ED] px-2.5 py-1.5 outline-none cursor-pointer"
            >
              {CAMPOS_BUSCA.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={termoBusca}
              onChange={e => setTermoBusca(e.target.value)}
              placeholder={campoDef.placeholder}
              className="flex-1 text-[12px] text-[#1A1D23] px-2.5 py-1.5 outline-none placeholder:text-[#9AA0AC]"
            />
            {termoBusca && (
              <button
                onClick={() => setTermoBusca('')}
                className="px-2 text-[#9AA0AC] hover:text-[#5F6470] text-sm"
              >
                ×
              </button>
            )}
          </div>

          {/* Contador */}
          <span className="text-[12px] text-[#5F6470] shrink-0">
            {termoBusca ? `${totalFiltrado} de ${totalOps}` : `${totalOps}`} operações
          </span>

          {carregando && (
            <div className="w-3.5 h-3.5 border-2 border-[#2E75B6] border-t-transparent rounded-full animate-spin shrink-0" />
          )}

          {/* Sincronizar iSolve */}
          <button
            onClick={sincronizar}
            disabled={sincronizando}
            title="Sincronizar propostas do iSolve"
            className="shrink-0 text-[12px] font-medium px-3 py-1.5 rounded-[6px] border border-[#E2E6ED] text-[#5F6470] hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {sincronizando ? (
              <span className="w-3 h-3 border-2 border-[#5F6470] border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Sincronizar
          </button>

          {/* Carga inicial (massa) - estabelece o watermark */}
          <button
            onClick={() => setConfirmCargaAberta(true)}
            disabled={rodandoMassa}
            title="Carga inicial: preenche Entrada iSolve com CCV assinado; não cria card no Kanban"
            className="shrink-0 text-[12px] font-medium px-3 py-1.5 rounded-[6px] border border-[#E2E6ED] text-[#5F6470] hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {rodandoMassa ? 'Carregando…' : 'Carga inicial'}
          </button>

          {/* Nova operação */}
          <button
            onClick={() => setModalNova(true)}
            className="ml-auto shrink-0 text-[12px] font-medium text-white px-3 py-1.5 rounded-[6px] transition-colors"
            style={{ backgroundColor: '#F49519' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#D97B0B')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#F49519')}
          >
            + Nova operação
          </button>
        </div>

        <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Título */}
          <div className="px-5 pt-3 pb-1">
            <h1 className="text-[13px] font-medium text-[#1A1D23]">Quadro de Operações</h1>
          </div>

        {erro && (
          <div className="mx-4 mt-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-[6px]">
            {erro}
          </div>
        )}

        {/* Kanban - scroll horizontal da faixa; scroll vertical DENTRO de cada coluna */}
        <DragDropContext onDragEnd={onDragEnd}>
          {/* overflow-x permite scroll horizontal; overflow-y:hidden impede scroll da página */}
          <div className="flex-1 overflow-x-auto" style={{ overflowY: 'hidden' }}>
            {/* min-w-max garante que as 3 colunas não colapsem; h-full estica até o pai */}
            <div className="flex gap-4 px-4 pb-4 pt-2 min-w-max h-full">
              {([0, 1, 2, 3] as Esteira[]).map((est) => (
                <KanbanColumn
                  key={est}
                  esteira={est}
                  operacoes={colunasFiltradas[est]}
                  onCardClick={abrirModal}
                  onEsteiraClick={(e) => navigate(`/esteira/${e}`)}
                />
              ))}
            </div>
          </div>
        </DragDropContext>
      </main>

      {modalNova && (
        <NovaOperacaoModal
          onClose={() => setModalNova(false)}
          onCriada={() => {
            setModalNova(false);
            carregarKanban();
          }}
          onImportadoSemCcv={(operacaoId) => {
            setModalNova(false);
            carregarKanban();
            navigate(`/operacoes/${operacaoId}`);
          }}
        />
      )}

      <OperacaoDetalheSheet
        operacaoId={opDetalheId}
        onClose={() => setOpDetalheId(null)}
        onUpdated={() => carregarKanban()}
      />

      <ConfirmModal
        aberto={confirmCargaAberta}
        titulo="Carga inicial"
        textoConfirmar="Rodar carga inicial"
        processando={rodandoMassa}
        mensagem={
          'Lista as operações com CCV assinado no iSolve e preenche a aba "Entrada iSolve".\n\n' +
          'Nenhuma operação entra no Quadro nesta etapa. Depois use "Sincronizar" para cadastrar o que for genuinamente novo, ou crie pela aba.'
        }
        onConfirmar={executarCargaMassa}
        onCancelar={() => setConfirmCargaAberta(false)}
      />
    </div>
  );
}
